import React from 'react';
import { useTranslation } from 'react-i18next';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Select from '@lib/components/Select/Select';
import { InputSizes } from '@lib/components/Input/enums';
import { TaskTimelineFrameEnum } from 'graphql-common';
import { revertTransformDate, transformDate } from '@lib/utils/dateTimeHelpers';
import {
  datePickerQuarterFormat,
  datePickerShortDateFormat,
  datePickerYearFormat,
} from '@lib/enums/dateTime';
import DatePicker from '@lib/components/DateTimePicker/DatePicker';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import { getTaskTimelineViewOptions } from 'utils/getTaskTimelineViewOptions';
import {
  getDecrementedNextEndDate,
  getIncrementedNextEndDate,
  getIsDecrementDisabled,
  getIsIncrementDisabled,
  getMaxDate,
  getMinDate,
  getPeriodEndDate,
  getPeriodStartDate,
} from './utils/getPeriodDate';
import styles from './TableTopHeadComponent.module.scss';

type Props = {
  endDate?: string | null;
  isFullTableView: boolean;
  onToggleFullTableView: () => void;
  onEndDateChange: (v: string) => void;
  onViewChange: (v: unknown) => void;
  periodEndParam?: string | null;
  periodStartParam?: string | null;
  view: TaskTimelineFrameEnum;
};

export default function TableTopHeadComponent(props: Props) {
  const {
    endDate,
    isFullTableView,
    onEndDateChange,
    onToggleFullTableView,
    onViewChange,
    periodEndParam,
    periodStartParam,
    view,
  } = props;
  const { t } = useTranslation();
  const options = getTaskTimelineViewOptions();
  const selectedOption = options.find(({ value }) => {
    return value === view;
  });

  const datePickerType = (() => {
    if (view === TaskTimelineFrameEnum.Year) {
      return 'year';
    }
    if (view === TaskTimelineFrameEnum.Quarter) {
      return 'quarter';
    }
    if (view === TaskTimelineFrameEnum.Month) {
      return 'month';
    }
    if (view === TaskTimelineFrameEnum.Week) {
      return 'week';
    }
    return undefined;
  })();

  const datePickerFormat = (() => {
    if (view === TaskTimelineFrameEnum.Year) {
      return datePickerYearFormat;
    }
    if (view === TaskTimelineFrameEnum.Quarter) {
      return datePickerQuarterFormat;
    }
    return datePickerShortDateFormat;
  })();

  const isDecrementDisabled = getIsDecrementDisabled(
    endDate,
    periodStartParam,
    view,
  );

  const isIncrementDisabled = getIsIncrementDisabled(
    endDate,
    periodEndParam,
    view,
  );

  const minDate = getMinDate(periodStartParam, view);

  const maxDate = getMaxDate(periodEndParam, view);

  const decrementEndDate = () => {
    if (endDate) {
      const nextEndDate = getDecrementedNextEndDate(
        revertTransformDate(endDate),
        view,
      );
      if (nextEndDate) onEndDateChange(transformDate(nextEndDate, true));
    }
  };

  const incrementEndDate = () => {
    if (endDate) {
      const nextEndDate = getIncrementedNextEndDate(
        revertTransformDate(endDate),
        view,
      );
      if (nextEndDate) onEndDateChange(transformDate(nextEndDate, true));
    }
  };

  const onDateChange = (v: string) => {
    const nextEndDate = v
      ? transformDate(getPeriodEndDate(v, view), true)
      : transformDate(getPeriodEndDate(new Date().toISOString(), view), true);
    if (nextEndDate) {
      onEndDateChange(nextEndDate);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.leftCell}>
        <CheckBoxSingleBool
          name="show-task-details"
          value={isFullTableView}
          options={[{ label: 'checked', value: 'checked' }]}
          onChange={onToggleFullTableView}
          label={t('show-task-details')}
        />
      </div>
      <div className={styles.rightCell}>
        <div className={styles.datePickerWrap}>
          <IconButton
            icon="chevron_left"
            rounded
            type={IconButtonTypes.SecondaryFilled}
            onClick={decrementEndDate}
            disabled={isDecrementDisabled}
          />
          <div>
            <DatePicker
              name="datePicker"
              format={datePickerFormat}
              onChange={onDateChange}
              pickerType={datePickerType}
              size={InputSizes.medium}
              value={
                endDate
                  ? transformDate(getPeriodStartDate(endDate, view))
                  : undefined
              }
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
          <IconButton
            icon="chevron_right"
            rounded
            type={IconButtonTypes.SecondaryFilled}
            onClick={incrementEndDate}
            disabled={isIncrementDisabled}
          />
        </div>
        <div>
          <Select
            name="view"
            value={selectedOption}
            isSearchable={false}
            isClearable={false}
            size={InputSizes.medium}
            onChange={onViewChange}
            options={options}
            menuPlacement="bottom"
            usePortal
          />
        </div>
      </div>
    </div>
  );
}
